/**
 * Storage Service – Supabase Storage wrapper
 * Buckets:
 *   - progress-photos  (public)  : ảnh before/after thi công
 *   - qa-documents     (private) : file QA/QC docs (image + PDF)
 *   - media-assets     (public)  : ảnh tổng hợp
 */
import { supabase } from '@/core/database/supabase';

export type StorageBucket = 'progress-photos' | 'qa-documents' | 'media-assets' | 'construction';

export interface UploadResult {
  path: string;       // full path trong bucket
  url: string;        // public URL (hoặc signed URL với private bucket)
  error: string | null;
}

export interface UploadOptions {
  bucket: StorageBucket;
  /** Thư mục con, VD: project_id hoặc 'before-after/projectId' */
  folder?: string;
  /** Tên file tùy chỉnh, mặc định = timestamp + original name */
  fileName?: string;
  /** Cachecontrol header */
  cacheControl?: string;
}

let cachedSettings: any = null;
let lastSettingsFetch = 0;

async function getGlobalLimits() {
  const now = Date.now();
  if (cachedSettings && now - lastSettingsFetch < 60000) return cachedSettings; // 1 phút cache
  
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;
    
    const { data: profile } = await supabase.from('user_profiles').select('company_id').eq('id', user.id).single();
    if (!profile?.company_id) return null;
    
    const { data: company } = await supabase.from('companies').select('settings').eq('id', profile.company_id).single();
    if (company?.settings) {
      cachedSettings = company.settings;
      lastSettingsFetch = now;
      return cachedSettings;
    }
  } catch (error) {
    console.warn('[StorageService] could not fetch global limits', error);
  }
  return null;
}

const storageService = {
  /**
   * Upload một file lên Supabase Storage
   */
  async upload(file: File, options: UploadOptions): Promise<UploadResult> {
    try {
      const ext = file.name.split('.').pop()?.toLowerCase() ?? 'bin';
      const ts = Date.now();
      const safeName = options.fileName
        ? `${options.fileName}.${ext}`
        : `${ts}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
      const path = options.folder ? `${options.folder}/${safeName}` : safeName;

      // Sử dụng type gốc của file thay vì ép về PNG
      const contentType = file.type || 'application/octet-stream';

      // ==========================================
      // GLOBAL VALIDATION GUARD (Settings)
      // ==========================================
      const settings = await getGlobalLimits();
      if (settings) {
         const allowedTypes: string[] = settings.allowed_file_types || ['PDF', 'DOC', 'DOCX', 'XLS', 'XLSX', 'DWG', 'JPG', 'PNG'];
         const fileExtUpper = file.name.split('.').pop()?.toUpperCase() ?? '';
         
         if (fileExtUpper && !allowedTypes.includes(fileExtUpper) && !allowedTypes.includes('*')) {
            throw new Error(`Định dạng tệp .${fileExtUpper} không được phép tải lên hệ thống.`);
         }
         
         const maxFileSizeStr = settings.max_file_size || '50 MB';
         const maxMb = parseInt(maxFileSizeStr) || 50;
         const fileSizeMb = file.size / (1024 * 1024);
         if (fileSizeMb > maxMb) {
            throw new Error(`Dung lượng tệp (${fileSizeMb.toFixed(1)} MB) vượt quá giới hạn cho phép (${maxMb} MB).`);
         }
      }
      // ==========================================

      const { error: uploadError } = await supabase.storage
        .from(options.bucket)
        .upload(path, file, {
          cacheControl: options.cacheControl ?? '3600',
          upsert: false,
          contentType: contentType,
        });

      if (uploadError) throw uploadError;

      // Lấy URL
      let url = '';
      if (options.bucket === 'qa-documents') {
        // Private bucket → signed URL mặc định 7 ngày (an toàn hơn 1 năm)
        const { data: signedData, error: signErr } = await supabase.storage
          .from(options.bucket)
          .createSignedUrl(path, 60 * 60 * 24 * 7);
        if (signErr) throw signErr;
        url = signedData.signedUrl;
      } else {
        // Public bucket
        const { data: publicData } = supabase.storage
          .from(options.bucket)
          .getPublicUrl(path);
        url = publicData.publicUrl;
      }

      return { path, url, error: null };
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Upload thất bại';
      console.error('[StorageService] upload error:', err);
      return { path: '', url: '', error: msg };
    }
  },

  /**
   * Lấy URL ảnh đã được tối ưu hóa (kích thước, chất lượng)
   * Sử dụng tính năng Image Transformation của Supabase
   */
  getOptimizedUrl(bucket: StorageBucket, path: string, options: { width?: number; height?: number; quality?: number } = {}): string {
    if (!path) return '';
    
    // Nếu là private bucket, việc transform cần signed URL phức tạp hơn, 
    // ở đây hỗ trợ chủ yếu cho public buckets (ảnh dự án, avatar)
    const { data } = supabase.storage.from(bucket).getPublicUrl(path, {
      transform: {
        width: options.width,
        height: options.height,
        quality: options.quality ?? 80,
        format: 'origin', // hoặc 'webp' nếu Supabase project hỗ trợ
      }
    });
    
    return data.publicUrl;
  },

  /**
   * Upload nhiều files cùng lúc
   */
  async uploadMultiple(
    files: File[],
    options: UploadOptions
  ): Promise<UploadResult[]> {
    return Promise.all(files.map(f => storageService.upload(f, options)));
  },

  /**
   * Xóa file khỏi bucket
   */
  async remove(bucket: StorageBucket, path: string): Promise<string | null> {
    try {
      const { error } = await supabase.storage.from(bucket).remove([path]);
      if (error) throw error;
      return null;
    } catch (err) {
      return err instanceof Error ? err.message : 'Xóa file thất bại';
    }
  },

  /**
   * Lấy public URL của file (chỉ dùng cho public buckets)
   */
  getPublicUrl(bucket: StorageBucket, path: string): string {
    const { data } = supabase.storage.from(bucket).getPublicUrl(path);
    return data.publicUrl;
  },

  /**
   * Tạo signed URL tạm thời (cho private buckets hoặc chia sẻ tạm)
   */
  async createSignedUrl(
    bucket: StorageBucket,
    path: string,
    expiresInSeconds = 3600
  ): Promise<string | null> {
    try {
      const { data, error } = await supabase.storage
        .from(bucket)
        .createSignedUrl(path, expiresInSeconds);
      if (error) throw error;
      return data.signedUrl;
    } catch {
      return null;
    }
  },

  /**
   * Shorthand: upload file với bucket + file + folder string
   */
  async uploadFile(bucket: StorageBucket, file: File, folder: string): Promise<UploadResult> {
    return storageService.upload(file, { bucket, folder });
  },

  /**
   * List files trong folder
   */
  async list(bucket: StorageBucket, folder: string) {
    const { data, error } = await supabase.storage.from(bucket).list(folder);
    if (error) throw error;
    return data ?? [];
  },
};

export { storageService };
export default storageService;
